/**
 * Client-side representation of a document during the upload flow.
 * Mirrors the Document entity shape from the BE — fields are populated in two phases:
 *
 * Phase 1 (file selected):
 *   fileName, mimeType, size, description, provider, previewUrl are set immediately.
 *
 * Phase 2 (after BE signed URL response):
 *   rk, signedUrl, url are set before the actual upload starts.
 *
 * Status lifecycle: pending -> ready -> uploading -> done | failed
 */
export default class UploadFileItem {

    /**
     * @param {File} file - native browser File object
     * @param {string} defaultProvider - storage provider key, e.g. 'r2', 'nextcloud'
     */
    constructor(file, defaultProvider) {
        // Phase 1 — available immediately after file select
        this.file = file;
        this.fileName = file.name;
        this.mimeType = file.type;

        // Size in KB — matches Document entity field
        this.size = Math.round(file.size / 1024);

        this.description = '';
        this.provider = defaultProvider ?? '';

        // Object URL for local image preview — revoked on remove or clear
        this.previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

        // Phase 2 — populated after BE signed URL response
        this.rk = null;
        this.signedUrl = null;

        // Definitive storage URL — set after successful upload confirmation from BE
        this.url = null;

        // pending | ready | uploading | done | failed
        this.status = 'pending';
    }

    /**
     * Unique deduplication key — name + size combo.
     * Used to prevent the same file from being added twice.
     * @returns {string}
     */
    getKey() {
        return `${this.fileName}_${this.size}`;
    }
}
